import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SharedTrip } from '../../types';
import type { ItineraryRouteDay } from '../../api/directions.api';
import { tripsApi } from '../../api/trips.api';
import { decodePolylinePoints } from '../../utils/polyline';
import ReadOnlyTripMap, { type TripMapPin, type TripMapStop } from '../../components/map/ReadOnlyTripMap';

/**
 * The shared trip on a map.
 *
 * Everything drawn here came in the payload: coordinates were already persisted
 * and the route was read from the directions cache server-side (`cachedOnly`).
 * This component must never resolve anything itself — there is no logged-in user
 * behind a share link, so there is nobody to charge and nobody to cap. The one
 * Google cost it does incur is the map load, and that is reported back so it
 * shows up as a measurement rather than as a surprise (ADR-0001).
 */

interface Props {
  trip: SharedTrip;
  token: string;
}

export default function SharedMap({ trip, token }: Props) {
  const { t } = useTranslation();
  const days = trip.days ?? [];
  const venues = trip.venues ?? [];
  const route: ItineraryRouteDay[] = trip.route ?? [];

  const [mode, setMode] = useState<'places' | 'route'>(venues.length > 0 ? 'places' : 'route');
  // ADR-0011 — a view never opens with several days at once. One day, the first.
  const [dayIdx, setDayIdx] = useState(0);

  const center = useMemo(() => {
    if (trip.destinationLat != null && trip.destinationLng != null) {
      return { lat: Number(trip.destinationLat), lng: Number(trip.destinationLng) };
    }
    const anchor = venues.find(v => v.latitude != null && v.longitude != null);
    return anchor ? { lat: Number(anchor.latitude), lng: Number(anchor.longitude) } : null;
  }, [trip.destinationLat, trip.destinationLng, venues]);

  const pins: TripMapPin[] = useMemo(() => (
    mode !== 'places' ? [] : venues
      .filter(v => v.latitude != null && v.longitude != null)
      .map(v => ({
        id: v.id,
        lat: Number(v.latitude),
        lng: Number(v.longitude),
        title: v.name,
        category: v.category,
      }))
  ), [mode, venues]);

  const day = days[dayIdx];
  const dayRoute = route.find(r => r.dayId === day?.id);

  const stops: TripMapStop[] = useMemo(() => (
    mode !== 'route' || !day ? [] : day.activities
      .filter(a => a.latitude != null && a.longitude != null)
      .map(a => ({
        id: a.id,
        lat: Number(a.latitude),
        lng: Number(a.longitude),
        title: a.venueName ?? a.title,
      }))
  ), [mode, day]);

  const paths = useMemo(() => (
    mode !== 'route' || !dayRoute ? [] : dayRoute.legs
      .filter((leg): leg is NonNullable<typeof leg> => leg !== null)
      // Decoded WITHOUT the Maps SDK on purpose: this memo runs before the map
      // below has loaded it, and `decodeEncodedPolyline` would reach into a
      // `window.google` that is not there yet.
      .map(leg => leg.segments.flatMap(seg => decodePolylinePoints(seg.polyline)))
  ), [mode, dayRoute]);

  const hasRoute = days.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        {venues.length > 0 && hasRoute && (['places', 'route'] as const).map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`h-7 px-3 rounded-full font-mono text-[10px] font-semibold tracking-wide transition-colors cursor-pointer ${
              mode === m
                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            {m === 'places' ? t('shared.map.allPlaces') : t('shared.map.dayByDay')}
          </button>
        ))}

        {mode === 'route' && (
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
            {days.map((d, i) => (
              <button
                key={d.id}
                onClick={() => setDayIdx(i)}
                className={`shrink-0 h-7 px-2.5 rounded-full font-mono text-[10px] font-semibold transition-colors cursor-pointer ${
                  i === dayIdx
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {t('shared.map.dayLabel', { n: d.dayNumber })}
              </button>
            ))}
          </div>
        )}
      </div>

      <ReadOnlyTripMap
        center={center}
        pins={pins}
        stops={stops}
        paths={paths}
        onMapLoad={() => tripsApi.reportSharedMapLoad(token)}
        unavailableLabel={t('shared.map.unavailable')}
      />

      {/* A day whose legs were never computed draws pins without a line. Saying
          so beats a map that looks broken. */}
      {mode === 'route' && stops.length > 1 && paths.length === 0 && (
        <p className="text-2xs text-slate-400 text-center">{t('shared.map.noRouteYet')}</p>
      )}
    </div>
  );
}
